// types
import type { StateTree } from '@makemydeal/dr-dash-types';

// selectors
import * as offerReduxSelectors from '../selectors/offerRedux';
import * as offerInfoSelectors from '../selectors/offerInfoSelectors';

/**
 * Single source of truth for the tail-telemetry readiness gate and business
 * context, shared by BOTH tail middlewares (`selectorDeltaTelemetryMiddleware`
 * and `stateSnapshotTelemetryMiddleware`).
 *
 * WHY THIS IS SHARED (recommendation #3): the two tail middlewares MUST flip
 * "ready" on the same dispatch and stamp the same business context, otherwise
 * their `actionIndex` join key and per-deal filtering can silently diverge.
 * Previously each wrapper inlined its own `waitForReady` / `getBusinessContext`
 * lambda, so the alignment was guaranteed only by a code comment. Importing one
 * predicate from here makes the two middlewares provably identical — a drift in
 * one can't happen without changing both.
 */

/**
 * Readiness predicate: telemetry defers counting/emitting until bootstrap has
 * populated `dealXgId` in state. Without this, the replay anchor (the first
 * emitted snapshot / `actionIndex === 1`) is emitted during the very first
 * pre-bootstrap dispatch and lands in New Relic with `dealXgId=""`, which the
 * Per-Deal Explorer nerdpack widget (filters by `dealXgId = ?`) excludes —
 * breaking per-deal replay for the whole session until the next cadenced anchor.
 */
export const telemetryReady = (state: StateTree): boolean => Boolean(offerReduxSelectors.getDealXgId(state));

/**
 * Business context echoed on every emitted telemetry event so replay tooling
 * can group by deal/offer without joining against another table.
 */
export const getTelemetryBusinessContext = (state: StateTree): Record<string, unknown> => ({
    dealXgId: offerReduxSelectors.getDealXgId(state),
    offerId: offerInfoSelectors.getOfferId(state)
});
